import React from 'react';
import { Button, InputField } from '@shared/components';
import { useAppDarkMode } from '../hooks/useAppDarkMode';
import UserSelector from '../components/authorized-users/UserSelector';
import { useAuthorizedUsers } from '../hooks/useAuthorizedUser';
import { Loader } from 'lucide-react';

const AuthorizedUsers: React.FC = () => {
  const {
    showUserForm,
    formData,
    authorizedUsers,
    isLoading,
    showAddUserForm,
    showEditUserForm,
    deleteUser,
    saveUser,
    cancel,
    handleChange,
    isSubmitting
  } = useAuthorizedUsers();

  const { darkMode } = useAppDarkMode();

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto p-6 pb-24 md:pb-6">
          <div className="space-y-4">

            {/* Action Buttons */}
            <div className="w-full">
              <button
                onClick={showAddUserForm}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create a New Authorized User
              </button>
            </div>

            {/* User Form */}
            {showUserForm && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  await saveUser();
                }}
              >
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow p-4 space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{formData.courierUserId ? 'Edit' : 'Add'} User</h3>

                  {/* Uncomment and use UserSelector if needed */}
                  {/* <UserSelector
                    value={formData.courierUserId}
                    onChange={handleCustomerSelect}
                    disabled={false}
                    initialCustomerName={formData.name}
                  /> */}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      darkMode={darkMode}
                      type="text"
                      name="name"
                      label="Name"
                      value={formData.name}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                    <InputField
                      darkMode={darkMode}
                      type="phone"
                      name="phone"
                      label="Phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      fullWidth
                    />
                    <InputField
                      darkMode={darkMode}
                      type="text"
                      name="idType"
                      label="ID Type"
                      value={formData.idType}
                      onChange={handleChange}
                      fullWidth
                    />
                    <InputField
                      darkMode={darkMode}
                      type="text"
                      name="idNumber"
                      label="ID Number"
                      value={formData.idNumber}
                      onChange={handleChange}
                      fullWidth
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="secondary" isLoading={isLoading} onClick={cancel}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!formData.name || !formData.phone || isSubmitting}
                      isLoading={isSubmitting}
                      color="#f97316" // orange-500
                      hoverColor="#f59e0b" // orange-600
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </form>
            )}

            {/* User List */}
            {isLoading && authorizedUsers.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 text-center">
              <Loader className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400">Loading users...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {authorizedUsers.map((user) => (
                <div
                  key={user.courierUserId}
                  className="bg-white border dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-lg shadow p-4 space-y-2"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-lg text-gray-900 dark:text-white">{user.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user.phone}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => showEditUserForm(user)} className="text-blue-500 hover:text-blue-700" aria-label={`Edit ${user.name}`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L14.732 3.732z" />
                        </svg>
                      </button>
                      <button onClick={() => deleteUser(user)} className="text-red-500 hover:text-red-700" aria-label={`Delete ${user.name}`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">ID Type:</span> {user.idType}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">ID Number:</span> {user.idNumber}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
    </div>
  );
};

export default AuthorizedUsers;
